import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { setStatus } from '../render/plan_renderer.js?v=20260904f';
import {
  OBSTACLE_MIN_DRAW_LENGTH,
  OBSTACLE_TYPES,
  deleteObstacle,
  deleteSelectedObstacles,
  ensureObstacleState,
  normalizeObstacle,
  obstacleActiveTool,
  obstacleById,
  obstacleConfigFromObstacle,
  pendingObstacleConfig,
  rotateObstacle,
  selectedObstacles,
  setPendingObstacleConfig,
  updateObstacle
} from './obstacles.js?v=20260903c';
import { recordUndo } from '../services/history_service.js?v=20260903c';
import { t } from '../i18n/i18n_service.js?v=20260722a';
import { escapeHtml } from '../utils/html_utils.js?v=20260706d';

/**
 * Rendert render Obstacle Selection Panel im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt DOM-UI und Seitenleisten.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function renderObstacleSelectionPanel() {
  const selected = selectedObstacles();
  const activeTool = obstacleActiveTool();
  const obstacle = selected[0] || (activeTool ? obstaclePendingPreview(activeTool) : null);
  if (!obstacle) return false;
  const host = document.getElementById('selectionPanel');
  if (!host) return false;
  const sameType = selected.length ? selected.every(entry => entry.type === selected[0].type) : true;
  if (selected.length > 1 && !sameType) {
    host.className = 'obstacle-selection-panel';
    host.innerHTML = obstacleMixedSelectionPanelHtml(selected);
    bindObstaclePanelEvents(null);
    return true;
  }
  const panelObstacle = selected.length > 1 ? obstacleCommonSelectionModel(selected) : obstacle;
  const mode = selected.length ? 'selection' : 'pending';
  host.className = 'obstacle-selection-panel';
  host.innerHTML = panelObstacle.type === 'pipe'
    ? obstaclePipePanelHtml(panelObstacle, { mode, count: selected.length })
    : obstacleDuctPanelHtml(panelObstacle, { mode, count: selected.length });
  bindObstaclePanelEvents(obstacle.id);
  return true;
}

function obstaclePendingPreview(type) {
  return normalizeObstacle({
    id: 'pending',
    type,
    start: { x: 0, z: 0 },
    end: { x: OBSTACLE_MIN_DRAW_LENGTH, z: 0 },
    ...pendingObstacleConfig(type)
  });
}

function obstacleCommonSelectionModel(obstacles) {
  const first = normalizeObstacle(obstacles[0]);
  const mixed = new Set();
  const fields = first.type === 'pipe'
    ? ['diameter', 'heightFromFloorToBottom']
    : ['width', 'height', 'heightFromFloorToBottom'];
  for (const field of fields) {
    const comparableValue = first[field];
    if (obstacles.some(obstacle => normalizeObstacle(obstacle)?.[field] !== comparableValue)) {
      mixed.add(field);
      first[field] = null;
    }
  }
  first._mixedFields = mixed;
  return first;
}

function obstacleIsMixed(obstacle, key) {
  return obstacle?._mixedFields instanceof Set && obstacle._mixedFields.has(key);
}

function obstacleNumberValue(obstacle, key) {
  const numericValue = Number(obstacle?.[key]);
  return obstacleIsMixed(obstacle, key) ? '' : Math.round(Number.isFinite(numericValue) ? numericValue : 0);
}

function obstaclePlaceholder(obstacle, key) {
  return obstacleIsMixed(obstacle, key) ? ' placeholder="unterschiedlich"' : '';
}

function obstacleDetailRow(label, controlHtml, className = '') {
  return `
    <div class="detail-row obstacle-detail-row ${className}">
      <div class="detail-row-label">${label}</div>
      <div class="detail-row-control">${controlHtml}</div>
    </div>
  `;
}

/**
 * Beschreibt obstacle Pipe Panel Html im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt DOM-UI und Seitenleisten.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} obstacle - Eingabewert für obstacle Pipe Panel Html.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstaclePipePanelHtml(obstacle, options = {}) {
  const pending = options.mode === 'pending';
  return `
    <div class="card">
      <div class="card-title">${escapeHtml(t('sidebar_rechts.rohr.titel.karte', 'Rohr'))}</div>
      <div class="small-text note">${pending ? 'Voreinstellung für neues Rohr' : `${options.count > 1 ? `${options.count} Rohre` : 'Hilfsobjekt'} · nicht in BOM und ohne Snap-Logik`}</div>
    </div>
    ${options.count > 1 ? '' : obstaclePipeDiagramHtml(obstacle)}
    <div class="card">
      <div class="card-title">${escapeHtml(t('sidebar_rechts.rohr.titel.masse', 'Masse'))}</div>
      <div class="obstacle-detail-grid">
        ${obstacleDetailRow(escapeHtml(t('sidebar_rechts.rohr.label.durchmesser', 'Durchmesser')), `<input id="obstacleDiameter" type="number" min="1" step="10" value="${obstacleNumberValue(obstacle, 'diameter')}"${obstaclePlaceholder(obstacle, 'diameter')}>`)}
        ${obstacleDetailRow(escapeHtml(t('sidebar_rechts.rohr.label.bodenabstand', 'Bodenabstand')), `<input id="obstacleBottom" type="number" min="0" step="10" value="${obstacleNumberValue(obstacle, 'heightFromFloorToBottom')}"${obstaclePlaceholder(obstacle, 'heightFromFloorToBottom')}>`)}
      </div>
    </div>
    ${obstacleHistoryHtml('pipe')}
    ${pending ? '' : `
      ${obstacleTransformPanelHtml()}
      <div class="card">
        <button id="btnDeleteObstacle" class="btn danger" type="button">${options.count > 1 ? escapeHtml(t('sidebar_rechts.rohr.button.loeschen_mehrzahl', 'Hindernisse löschen')) : escapeHtml(t('sidebar_rechts.rohr.button.loeschen', 'Hindernis löschen'))}</button>
      </div>
    `}
  `;
}

/**
 * Beschreibt obstacle Duct Panel Html im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt DOM-UI und Seitenleisten.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} obstacle - Eingabewert für obstacle Duct Panel Html.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstacleDuctPanelHtml(obstacle, options = {}) {
  const pending = options.mode === 'pending';
  return `
    <div class="card">
      <div class="card-title">${escapeHtml(t('sidebar_rechts.lueftung.titel.karte', 'Lüftung'))}</div>
      <div class="small-text note">${pending ? 'Voreinstellung für neue Lüftung' : `${options.count > 1 ? `${options.count} Lüftungen` : 'Hilfsobjekt'} · nicht in BOM und ohne Snap-Logik`}</div>
    </div>
    ${options.count > 1 ? '' : obstacleDuctDiagramHtml(obstacle)}
    <div class="card">
      <div class="card-title">${escapeHtml(t('sidebar_rechts.lueftung.titel.masse', 'Masse'))}</div>
      <div class="obstacle-detail-grid">
        ${obstacleDetailRow(escapeHtml(t('sidebar_rechts.lueftung.label.breite', 'Breite')), `<input id="obstacleWidth" type="number" min="1" step="10" value="${obstacleNumberValue(obstacle, 'width')}"${obstaclePlaceholder(obstacle, 'width')}>`)}
        ${obstacleDetailRow(escapeHtml(t('sidebar_rechts.lueftung.label.hoehe', 'Höhe')), `<input id="obstacleHeight" type="number" min="1" step="10" value="${obstacleNumberValue(obstacle, 'height')}"${obstaclePlaceholder(obstacle, 'height')}>`)}
        ${obstacleDetailRow(escapeHtml(t('sidebar_rechts.lueftung.label.bodenabstand', 'Bodenabstand')), `<input id="obstacleBottom" type="number" min="0" step="10" value="${obstacleNumberValue(obstacle, 'heightFromFloorToBottom')}"${obstaclePlaceholder(obstacle, 'heightFromFloorToBottom')}>`)}
      </div>
    </div>
    ${obstacleHistoryHtml('duct')}
    ${pending ? '' : `
      ${obstacleTransformPanelHtml()}
      <div class="card">
        <button id="btnDeleteObstacle" class="btn danger" type="button">${options.count > 1 ? escapeHtml(t('sidebar_rechts.rohr.button.loeschen_mehrzahl', 'Hindernisse löschen')) : escapeHtml(t('sidebar_rechts.rohr.button.loeschen', 'Hindernis löschen'))}</button>
      </div>
    `}
  `;
}

function obstacleMixedSelectionPanelHtml(obstacles) {
  return `
    <div class="card">
      <div class="card-title">${escapeHtml(t('sidebar_rechts.hindernisse.titel.karte', 'Hindernisse'))}</div>
      <div class="small-text note">${obstacles.length} Hindernisse unterschiedlicher Typen ausgewählt.</div>
    </div>
    <div class="card">
      <button id="btnDeleteObstacle" class="btn danger" type="button">${escapeHtml(t('sidebar_rechts.rohr.button.loeschen_mehrzahl', 'Hindernisse löschen'))}</button>
    </div>
  `;
}

function obstacleHistoryHtml(type) {
  const items = obstacleUniqueHistoryItems(type);
  const body = items.length
    ? items.map(obstacle => `
        <button class="btn obstacle-history-item" type="button" data-obstacle-history-id="${escapeHtml(obstacle.id)}">
          ${escapeHtml(obstacleHistoryLabel(obstacle))}
        </button>
      `).join('')
    : '<div class="small-text note">Noch keine Vorlage vorhanden.</div>';
  return `
    <div class="card obstacle-history-card">
      <div class="card-title">${escapeHtml(t('sidebar_rechts.history.titel.karte', 'History'))}</div>
      <div class="stack">${body}</div>
    </div>
  `;
}

function obstacleUniqueHistoryItems(type) {
  const state = ensureObstacleState();
  const unique = new Map();
  [...state].reverse()
    .filter(obstacle => obstacle.type === type)
    .forEach(obstacle => {
      const signature = getObstacleSignature(obstacle);
      if (signature && !unique.has(signature)) unique.set(signature, obstacle);
    });
  return Array.from(unique.values());
}

function getObstacleSignature(obstacle) {
  const normalized = normalizeObstacle(obstacle);
  if (!normalized) return '';
  if (normalized.type === 'pipe') {
    return [
      'pipe',
      normalized.diameter,
      normalized.heightFromFloorToBottom,
      normalized.material || '',
      normalized.color || ''
    ].join('|');
  }
  if (normalized.type === 'duct') {
    return [
      'vent',
      normalized.width,
      normalized.depth || '',
      normalized.height,
      normalized.heightFromFloorToBottom
    ].join('|');
  }
  return '';
}

function obstacleHistoryLabel(obstacle) {
  if (obstacle.type === 'pipe') {
    return `Ø ${Math.round(obstacle.diameter)} · UK ${Math.round(obstacle.heightFromFloorToBottom)}`;
  }
  return `${Math.round(obstacle.width)} x ${Math.round(obstacle.height)} · UK ${Math.round(obstacle.heightFromFloorToBottom)}`;
}

/**
 * Beschreibt obstacle Transform Panel Html im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt DOM-UI und Seitenleisten.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstacleTransformPanelHtml() {
  return `
    <div class="card">
      <div class="card-title">${escapeHtml(t('sidebar_rechts.position.titel.karte', 'Position'))}</div>
      <div class="small-text note">Im Plan direkt ziehen oder hier um 90° drehen.</div>
      <div class="obstacle-transform-actions">
        <button id="btnRotateObstacleLeft" class="btn" type="button">${escapeHtml(t('sidebar_rechts.eigenschaften.button.rotieren_links', '90° links'))}</button>
        <button id="btnRotateObstacleRight" class="btn" type="button">${escapeHtml(t('sidebar_rechts.eigenschaften.button.rotieren_rechts', '90° rechts'))}</button>
      </div>
    </div>
  `;
}

/**
 * Beschreibt obstacle Pipe Diagram Html im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt DOM-UI und Seitenleisten.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} obstacle - Eingabewert für obstacle Pipe Diagram Html.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstaclePipeDiagramHtml(obstacle) {
  const bottom = Math.round(obstacle.heightFromFloorToBottom);
  const diameter = Math.round(obstacle.diameter);
  return `
    <div class="card obstacle-diagram-card">
      <div class="card-title">${escapeHtml(t('sidebar_rechts.ansicht.titel.karte', 'Ansicht'))}</div>
      <svg class="obstacle-diagram" viewBox="0 0 260 180" aria-label="Rohr Visualisierung">
        <line x1="24" y1="150" x2="236" y2="150" stroke="#111827" stroke-width="2" />
        <text x="24" y="168" fill="#374151" font-size="11">${escapeHtml(t('sidebar_rechts.ansicht.wert.boden', 'Boden'))}</text>
        <line x1="70" y1="150" x2="70" y2="58" stroke="#94a3b8" stroke-width="2" stroke-dasharray="4 4" />
        <circle cx="158" cy="58" r="28" fill="#e0f2fe" stroke="#0284c7" stroke-width="3" />
        <line x1="122" y1="86" x2="204" y2="86" stroke="#0284c7" stroke-width="2" />
        <text x="80" y="104" fill="#334155" font-size="11">Unterkante ${bottom} mm</text>
        <text x="126" y="52" fill="#0f172a" font-size="11">Ø ${diameter} mm</text>
      </svg>
    </div>
  `;
}

/**
 * Beschreibt obstacle Duct Diagram Html im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt DOM-UI und Seitenleisten.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} obstacle - Eingabewert für obstacle Duct Diagram Html.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstacleDuctDiagramHtml(obstacle) {
  const bottom = Math.round(obstacle.heightFromFloorToBottom);
  const width = Math.round(obstacle.width);
  const height = Math.round(obstacle.height);
  return `
    <div class="card obstacle-diagram-card">
      <div class="card-title">${escapeHtml(t('sidebar_rechts.ansicht.titel.karte', 'Ansicht'))}</div>
      <svg class="obstacle-diagram" viewBox="0 0 260 180" aria-label="Lüftung Visualisierung">
        <text x="161" y="28" fill="#334155" font-size="11" text-anchor="middle">Breite ${width} mm</text>
        <line x1="112" y1="38" x2="210" y2="38" stroke="#94a3b8" stroke-width="1.5" />
        <line x1="112" y1="34" x2="112" y2="42" stroke="#94a3b8" stroke-width="1.5" />
        <line x1="210" y1="34" x2="210" y2="42" stroke="#94a3b8" stroke-width="1.5" />
        <rect x="112" y="58" width="98" height="42" fill="#ede9fe" stroke="#7c3aed" stroke-width="3" />
        <text x="216" y="82" fill="#334155" font-size="11">${height} mm</text>
        <line x1="24" y1="150" x2="236" y2="150" stroke="#111827" stroke-width="2" />
        <text x="24" y="168" fill="#374151" font-size="11">${escapeHtml(t('sidebar_rechts.ansicht.wert.boden', 'Boden'))}</text>
        <line x1="161" y1="100" x2="161" y2="150" stroke="#94a3b8" stroke-width="2" stroke-dasharray="4 4" />
        <text x="161" y="124" fill="#334155" font-size="11" text-anchor="middle">Unterkante ${bottom} mm</text>
      </svg>
    </div>
  `;
}

/**
 * Beschreibt bind Obstacle Panel Events im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt DOM-UI und Seitenleisten.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} obstacleId - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * Seiteneffekte: Beachtet fachliche Hilfslogik im jeweiligen Modul; Änderungen erfolgen je nach Aufrufer am übergebenen Objekt oder globalen State.
 */
function bindObstaclePanelEvents(obstacleId) {
  const activeType = obstacleActiveTool();
  const selected = selectedObstacles();
  const selectedType = selected.length && selected.every(obstacle => obstacle.type === selected[0].type)
    ? selected[0].type
    : null;
  const targetType = selectedType || activeType;
  const applyPatch = (patch, label = 'Hindernis') => {
    if (!patch || !targetType) return;
    if (selected.length) {
      recordUndo(`${label} ändern`);
      selected
        .filter(obstacle => obstacle.type === targetType)
        .forEach(obstacle => updateObstacle(obstacle.id, patch));
      setStatus(`${label} aktualisiert`);
    } else {
      setPendingObstacleConfig(targetType, patch);
      setStatus(`${OBSTACLE_TYPES[targetType].label} Voreinstellung aktualisiert`);
    }
    AppRuntime.refresh(false);
  };
  const bindNumber = (id, key) => {
    const input = document.getElementById(id);
    if (!input) return;
    input.addEventListener('change', event => {
      const numericValue = Number(event.target.value);
      const value = Math.max(0, Number.isFinite(numericValue) ? numericValue : 0);
      applyPatch({ [key]: value }, 'Hindernis');
    });
  };
  bindNumber('obstacleDiameter', 'diameter');
  bindNumber('obstacleWidth', 'width');
  bindNumber('obstacleHeight', 'height');
  bindNumber('obstacleBottom', 'heightFromFloorToBottom');

  const rotateTargets = angle => {
    const targets = selected.length
      ? selected
      : (obstacleId ? [obstacleById(obstacleId)].filter(Boolean) : []);
    if (!targets.length) return;
    recordUndo('Hindernis drehen');
    targets.forEach(obstacle => rotateObstacle(obstacle.id, angle));
    setStatus(targets.length > 1 ? `${targets.length} Hindernisse 90° gedreht` : 'Hindernis 90° gedreht');
    AppRuntime.refresh(false);
  };

  document.getElementById('btnRotateObstacleLeft')?.addEventListener('click', () => {
    rotateTargets(-90);
  });
  document.getElementById('btnRotateObstacleRight')?.addEventListener('click', () => {
    rotateTargets(90);
  });

  document.getElementById('btnDeleteObstacle')?.addEventListener('click', () => {
    recordUndo('Hindernis löschen');
    if (selected.length) deleteSelectedObstacles();
    else if (obstacleId) deleteObstacle(obstacleId);
    setStatus(selected.length > 1 ? `${selected.length} Hindernisse gelöscht` : 'Hindernis gelöscht');
    AppRuntime.refresh(false);
  });

  document.querySelectorAll('[data-obstacle-history-id]').forEach(button => {
    button.addEventListener('click', () => {
      const source = obstacleById(button.dataset.obstacleHistoryId);
      if (!source) return;
      const patch = obstacleConfigFromObstacle(source);
      applyPatch(patch, OBSTACLE_TYPES[source.type]?.label || 'Hindernis');
    });
  });
}

export {
  renderObstacleSelectionPanel,
  obstaclePendingPreview,
  obstacleCommonSelectionModel,
  obstacleIsMixed,
  obstacleNumberValue,
  obstaclePlaceholder,
  obstacleDetailRow,
  obstaclePipePanelHtml,
  obstacleDuctPanelHtml,
  obstacleMixedSelectionPanelHtml,
  obstacleHistoryHtml,
  obstacleUniqueHistoryItems,
  getObstacleSignature,
  obstacleHistoryLabel,
  obstacleTransformPanelHtml,
  obstaclePipeDiagramHtml,
  obstacleDuctDiagramHtml,
  bindObstaclePanelEvents
};
